import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getOrganization } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Building2,
  Mail,
  CreditCard,
  Coins,
  Users,
  PlusCircle,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { AddCreditsModal } from "@/components/AddCreditsModal";
import { CreditTransactionList } from "@/components/CreditTransactionList";
import type { AcquisitionData, CreditBalance } from "@/lib/api";

function AcquisitionInfo({ data }: { data: AcquisitionData }) {
  const items = [
    { label: "Source", value: data.utm_source },
    { label: "Medium", value: data.utm_medium },
    { label: "Campaign", value: data.utm_campaign },
    { label: "Content", value: data.utm_content },
    { label: "Term", value: data.utm_term },
    { label: "Landing", value: data.landing_page },
    { label: "Referrer", value: data.referrer_url },
    { label: "Device", value: data.device_type },
    { label: "GCLID", value: data.gclid },
    { label: "FBCLID", value: data.fbclid },
  ].filter((item) => item.value);

  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">No data</p>;
  }

  return (
    <dl className="space-y-1 text-sm">
      {items.map(({ label, value }) => (
        <div key={label} className="flex gap-2">
          <dt className="text-muted-foreground w-20 flex-shrink-0">{label}:</dt>
          <dd className="font-medium truncate" title={value!}>
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function OrganizationDetail() {
  const { id } = useParams<{ id: string }>();

  // Modal state for credit adjustment
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState<CreditBalance | null>(
    null,
  );

  // Expandable credits state
  const [expandedCredits, setExpandedCredits] = useState<Set<string>>(
    new Set(),
  );

  const toggleCreditExpanded = (creditId: string) => {
    setExpandedCredits((prev) => {
      const next = new Set(prev);
      if (next.has(creditId)) {
        next.delete(creditId);
      } else {
        next.add(creditId);
      }
      return next;
    });
  };

  const handleOpenCreditModal = (credit: CreditBalance) => {
    setSelectedCredit(credit);
    setCreditModalOpen(true);
  };

  const {
    data: organization,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["organization", id],
    queryFn: () => getOrganization(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/organizations">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Organizations
          </Link>
        </Button>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          Error: {error.message}
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/organizations">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Organizations
          </Link>
        </Button>
        <div className="rounded-lg border p-4 text-muted-foreground">
          Organization not found
        </div>
      </div>
    );
  }

  const statusVariant = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "canceled":
      case "past_due":
        return "destructive";
      default:
        return "secondary";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/organizations">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Link>
        </Button>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">
              {organization.name || organization.email}
            </h1>
            {!organization.is_active && (
              <Badge variant="destructive">
                Deactivated{" "}
                {organization.deactivated_at
                  ? formatDate(organization.deactivated_at)
                  : ""}
              </Badge>
            )}
          </div>
          {organization.name && (
            <p className="text-muted-foreground">{organization.email}</p>
          )}
        </div>
      </div>

      {/* Organization Info */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{organization.email}</p>
            </div>
            {organization.name && (
              <div>
                <p className="text-sm text-muted-foreground">Name</p>
                <p className="font-medium">{organization.name}</p>
              </div>
            )}
            {organization.billing_email &&
              organization.billing_email !== organization.email && (
                <div>
                  <p className="text-sm text-muted-foreground">Billing Email</p>
                  <p className="font-medium">{organization.billing_email}</p>
                </div>
              )}
            <div>
              <p className="text-sm text-muted-foreground">
                Organization Since
              </p>
              <p className="font-medium">
                {formatDate(organization.created_at)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Company Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {organization.company_name ? (
              <>
                <div>
                  <p className="text-sm text-muted-foreground">Company Name</p>
                  <p className="font-medium">{organization.company_name}</p>
                </div>
                {organization.company_id && (
                  <div>
                    <p className="text-sm text-muted-foreground">ICO</p>
                    <p className="font-medium">{organization.company_id}</p>
                  </div>
                )}
                {organization.vat_id && (
                  <div>
                    <p className="text-sm text-muted-foreground">DIC</p>
                    <p className="font-medium">{organization.vat_id}</p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                No company information
              </p>
            )}
            {organization.stripe_customer_id && (
              <div>
                <p className="text-sm text-muted-foreground">Stripe Customer</p>
                <a
                  href={`https://dashboard.stripe.com/customers/${organization.stripe_customer_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:underline"
                >
                  {organization.stripe_customer_id}
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Subscriptions, Credits, Payments */}
      <Tabs defaultValue="subscriptions">
        <TabsList>
          <TabsTrigger
            value="subscriptions"
            className="flex items-center gap-2"
          >
            <CreditCard className="h-4 w-4" />
            Subscriptions ({organization.subscriptions.length})
          </TabsTrigger>
          <TabsTrigger value="credits" className="flex items-center gap-2">
            <Coins className="h-4 w-4" />
            Credits ({organization.credit_balances.length})
          </TabsTrigger>
          <TabsTrigger value="payments">
            Payments ({organization.payments.length})
          </TabsTrigger>
          <TabsTrigger value="accounts" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Accounts ({organization.service_accounts.length})
          </TabsTrigger>
        </TabsList>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions">
          <Card>
            <CardContent className="pt-6">
              {organization.subscriptions.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No active subscriptions
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Service</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Limits</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Billing</TableHead>
                      <TableHead>Period End</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {organization.subscriptions.map((sub) => (
                      <TableRow key={sub.id}>
                        <TableCell>
                          <Badge variant="outline">{sub.service_name}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          {sub.plan_name}
                        </TableCell>
                        <TableCell>
                          {sub.limits ? (
                            <div className="text-sm space-y-0.5">
                              {Object.entries(sub.limits).map(
                                ([key, value]) => (
                                  <div
                                    key={key}
                                    className="text-muted-foreground"
                                  >
                                    <span className="capitalize">
                                      {key.replace(/_/g, " ")}:
                                    </span>{" "}
                                    <span className="font-medium text-foreground">
                                      {typeof value === "boolean"
                                        ? value
                                          ? "Yes"
                                          : "No"
                                        : value}
                                    </span>
                                  </div>
                                ),
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusVariant(sub.status)}>
                            {sub.status}
                            {sub.cancel_at_period_end && " (canceling)"}
                          </Badge>
                        </TableCell>
                        <TableCell className="capitalize">
                          {sub.billing_interval}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {sub.current_period_end
                            ? formatDate(sub.current_period_end)
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Credits Tab */}
        <TabsContent value="credits">
          <Card>
            <CardContent className="pt-6">
              {organization.credit_balances.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No credit balances
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[30px]"></TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead>Current Balance</TableHead>
                      <TableHead>Lifetime Purchased</TableHead>
                      <TableHead>Lifetime Used</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {organization.credit_balances.map((credit) => {
                      const isExpanded = expandedCredits.has(credit.id);
                      return (
                        <React.Fragment key={credit.id}>
                          <TableRow
                            className={`cursor-pointer hover:bg-muted/50${!credit.account_name ? " opacity-50" : ""}`}
                            onClick={() => toggleCreditExpanded(credit.id)}
                          >
                            <TableCell className="w-[30px]">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {credit.service_name}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {credit.account_name ||
                                  credit.external_account_id.slice(0, 8) +
                                    "..."}
                                {!credit.account_name && (
                                  <Badge
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    Removed
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="font-medium text-lg">
                              {credit.balance}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {credit.lifetime_purchased}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {credit.lifetime_used}
                            </TableCell>
                            <TableCell
                              className="text-right"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenCreditModal(credit)}
                              >
                                <PlusCircle className="mr-2 h-4 w-4" />
                                Adjust
                              </Button>
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <tr>
                              <td colSpan={7} className="p-0">
                                <div className="bg-muted/30 border-t px-8 py-2">
                                  <CreditTransactionList
                                    serviceCode={credit.service_code}
                                    externalAccountId={
                                      credit.external_account_id
                                    }
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card>
            <CardContent className="pt-6">
              {organization.payments.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No payment history
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Fee</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Invoice</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {organization.payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell className="text-muted-foreground">
                          {payment.paid_at
                            ? formatDate(payment.paid_at)
                            : formatDate(payment.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {payment.service_name}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(payment.amount, payment.currency)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {payment.fee_amount
                            ? `-${formatCurrency(payment.fee_amount, payment.fee_currency ?? "EUR")}`
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              payment.status === "succeeded"
                                ? "success"
                                : "secondary"
                            }
                          >
                            {payment.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {payment.fakturoid_invoice_url && (
                              <a
                                href={payment.fakturoid_invoice_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline"
                              >
                                Fakturoid
                              </a>
                            )}
                            {payment.stripe_invoice_url && (
                              <a
                                href={payment.stripe_invoice_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline"
                              >
                                Stripe
                              </a>
                            )}
                            {!payment.fakturoid_invoice_url &&
                              !payment.stripe_invoice_url && (
                                <span className="text-muted-foreground">-</span>
                              )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Service Accounts Tab */}
        <TabsContent value="accounts">
          <Card>
            <CardContent className="pt-6">
              {organization.service_accounts.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No service accounts
                </p>
              ) : (
                <div className="space-y-6">
                  {organization.service_accounts.map((account) => (
                    <div
                      key={account.id}
                      className="border rounded-lg p-4 space-y-4"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <Badge variant="outline">
                            {account.service_name}
                          </Badge>
                          <p className="mt-1 font-medium">
                            {account.account_name ||
                              account.external_account_id}
                          </p>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(account.created_at)}
                        </p>
                      </div>

                      {/* Acquisition Data */}
                      {(account.first_touch || account.last_touch) && (
                        <div className="grid md:grid-cols-2 gap-4 pt-2 border-t">
                          {account.first_touch && (
                            <div>
                              <p className="text-sm font-medium text-muted-foreground mb-2">
                                First Touch
                              </p>
                              <AcquisitionInfo data={account.first_touch} />
                            </div>
                          )}
                          {account.last_touch && (
                            <div>
                              <p className="text-sm font-medium text-muted-foreground mb-2">
                                Last Touch
                              </p>
                              <AcquisitionInfo data={account.last_touch} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Credit Adjustment Modal */}
      {selectedCredit && (
        <AddCreditsModal
          open={creditModalOpen}
          onOpenChange={setCreditModalOpen}
          creditBalanceId={selectedCredit.id}
          organizationId={id!}
          serviceName={selectedCredit.service_name}
          accountName={selectedCredit.account_name}
          currentBalance={selectedCredit.balance}
          onSuccess={() => setSelectedCredit(null)}
        />
      )}
    </div>
  );
}

// Backwards compatibility export
export { OrganizationDetail as CustomerDetail };
